import { useState, useEffect } from "react";
import { API, useAuth , authFetch, useEditMode } from "@/App";
import { toast } from "sonner";
import { Target, Plus, TrendingUp } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { RegionSwitcher } from "@/components/RegionSwitcher";
import { useRegionFilter } from "@/hooks/useRegionFilter";
import { TargetDetailDialog } from "@/components/TargetDetailDialog";

const TARGET_TYPES = ["SALES", "COLLECTION", "VISITS", "ORDERS"];
const UNITS = ["BAGS", "MT", "INR", "COUNT"];

const TargetsPage = () => {
  const { user } = useAuth();
  const canEdit = useEditMode();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const { activeRegion, setActiveRegion, filterByRegion } = useRegionFilter();
  const [targets, setTargets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [detailOpen, setDetailOpen] = useState(null);
  const [form, setForm] = useState({ employee_id: "", target_type: "SALES", period: new Date().toISOString().slice(0, 7), target_value: 0, unit: "BAGS", description: "" });

  const fetchData = async () => {
    try {
      const [tRes, eRes] = await Promise.all([
        authFetch(`${API}/targets`, {  }),
        authFetch(`${API}/employees?status=ACTIVE`, {  }),
      ]);
      const [tData, eData] = await Promise.all([tRes.json(), eRes.json()]);
      if (tData.success) setTargets(tData.data);
      if (eData.success) setEmployees(eData.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const res = await authFetch(`${API}/targets`, { method: "POST", headers: { "Content-Type": "application/json" },  body: JSON.stringify(form) });
      const data = await res.json();
      if (data.success) { toast.success("Target created"); setShowCreate(false); fetchData(); }
      else toast.error(data.detail || "Failed");
    } catch { toast.error("Network error"); }
  };

  const handleUpdate = async (targetId, updates) => {
    try {
      const res = await authFetch(`${API}/targets/${targetId}`, { method: "PATCH", headers: { "Content-Type": "application/json" },  body: JSON.stringify(updates) });
      const data = await res.json();
      if (data.success) { toast.success("Target updated"); fetchData(); }
      else toast.error(data.detail || "Failed");
    } catch { toast.error("Network error"); }
  };

  return (
    <div className="space-y-6" data-testid="targets-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Sora']">Targets (Employees)</h1>
          <p className="text-slate-500 text-sm mt-1">Set and track performance targets for field employees</p>
        </div>
        <div className="flex items-center gap-2">
          <RegionSwitcher
            active={activeRegion}
            onChange={setActiveRegion}
            isSuperAdmin={isSuperAdmin}
            dataTestId="targets-region-switcher"
          />
        {isSuperAdmin && canEdit && (
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <button className="flex items-center gap-2 px-4 py-2 bg-[#8B1A1A] text-white rounded-lg hover:bg-[#6B1515] text-sm font-medium" data-testid="create-target-btn">
                <Plus className="w-4 h-4" /> Set Target
              </button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader><DialogTitle className="font-['Sora']">Set Employee Target</DialogTitle></DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 mt-4" data-testid="create-target-form">
                <div>
                  <label className="text-sm font-medium text-slate-700">Employee</label>
                  <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Select employee" /></SelectTrigger>
                    <SelectContent>{employees.map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{e.full_name} ({e.employee_code})</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Type</label>
                    <Select value={form.target_type} onValueChange={(v) => setForm({ ...form, target_type: v })}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{TARGET_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Period</label>
                    <Input type="month" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} className="mt-1" required data-testid="target-period" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Target Value</label>
                    <Input type="number" min="0" value={form.target_value} onChange={(e) => setForm({ ...form, target_value: parseFloat(e.target.value) })} className="mt-1" required />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Unit</label>
                    <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Description</label>
                  <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. Monthly sales target" className="mt-1" />
                </div>
                <button type="submit" className="w-full py-2.5 bg-[#8B1A1A] text-white rounded-lg font-medium hover:bg-[#6B1515]" data-testid="submit-target-btn">Create Target</button>
              </form>
            </DialogContent>
          </Dialog>
        )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40"><div className="w-8 h-8 spinner" /></div>
      ) : filterByRegion(targets, (t) => {
            const emp = employees.find((e) => e.employee_id === t.employee_id);
            return t.region_id || emp?.region_id;
          }).length === 0 ? (
        <Card className="p-8 text-center">
          <Target className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p className="text-lg font-medium text-slate-600">No targets set yet</p>
          <p className="text-sm text-slate-400 mt-1">Click "Set Target" to create a performance target for an employee</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="targets-grid">
          {filterByRegion(targets, (t) => {
            const emp = employees.find((e) => e.employee_id === t.employee_id);
            return t.region_id || emp?.region_id;
          }).map(t => {
            const pct = t.achievement_pct || 0;
            const barColor = pct >= 100 ? "bg-emerald-500" : pct >= 50 ? "bg-blue-500" : "bg-amber-500";
            return (
              <Card key={t.target_id} className="p-4 cursor-pointer hover:shadow-md transition-shadow" data-testid={`target-card-${t.target_id}`} onClick={() => setDetailOpen(t)}>
                <div className="flex items-center justify-between mb-3">
                  <Badge variant="outline" className="text-xs">{t.target_type}</Badge>
                  <Badge className={`text-xs ${pct >= 100 ? "bg-emerald-100 text-emerald-700" : pct >= 50 ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}`}>
                    {pct}%
                  </Badge>
                </div>
                <p className="text-sm font-medium text-slate-900">{t.employee_name}</p>
                <p className="text-xs text-slate-500">{t.employee_code} &middot; {t.period}</p>
                {t.description && <p className="text-xs text-slate-600 mt-1">{t.description}</p>}
                <div className="mt-3">
                  <div className="flex justify-between text-xs text-slate-500 mb-1">
                    <span>Achieved: {t.achieved_value} {t.unit}</span>
                    <span>Target: {t.target_value} {t.unit}</span>
                  </div>
                  <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full ${barColor} rounded-full transition-all`} style={{ width: `${Math.min(pct, 100)}%` }} />
                  </div>
                </div>
                {isSuperAdmin && (
                  <div className="mt-3 pt-3 border-t border-slate-100 flex gap-2" onClick={(e) => e.stopPropagation()}>
                    <Input type="number" min="0" placeholder="Update achieved"
                      className="h-8 text-xs flex-1"
                      onKeyDown={(e) => { if (e.key === "Enter") handleUpdate(t.target_id, { achieved_value: parseFloat(e.target.value) }); }}
                      data-testid={`update-achieved-${t.target_id}`}
                    />
                    <button onClick={() => handleUpdate(t.target_id, { status: t.status === "ACTIVE" ? "COMPLETED" : "ACTIVE" })}
                      className="px-2 py-1 text-xs rounded bg-slate-100 hover:bg-slate-200">
                      {t.status === "ACTIVE" ? "Complete" : "Reopen"}
                    </button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {detailOpen && (
        <TargetDetailDialog
          open={!!detailOpen}
          onClose={() => setDetailOpen(null)}
          target={detailOpen}
          employees={employees}
        />
      )}
    </div>
  );
};

export default TargetsPage;
